import IntranetLayout from "@/components/IntranetLayout";
import { useState } from "react";
import { Newspaper, Archive, PenTool, Settings, RefreshCw, Mail, Download, Star, Copy, Globe, ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

/* ── Digest Stories ── */
interface Story {
  section: string; sectionIcon: string; date: string; title: string; summary: string;
  source: string; relevance: number; implication: string;
}

const STORIES: Story[] = [
  { section: "MARKET WATCH — GUYANA", sectionIcon: "🇬🇾", date: "Apr 14, 2026", title: "ExxonMobil Approves Hammerhead FPSO Final Investment Decision", summary: "ExxonMobil's fourth Guyana FPSO project has received FID approval, adding 250,000 bpd production capacity to the Stabroek Block. CTV demand expected to increase by Q4 2026.", source: "Offshore Energy Today", relevance: 5, implication: "Direct demand trigger for additional CTV capacity — accelerate ExxonMobil engagement" },
  { section: "MARKET WATCH — GUYANA", sectionIcon: "🇬🇾", date: "Apr 12, 2026", title: "GSBI Shore Base Expands Berth Capacity", summary: "Guyana Shore Base Inc. completed Phase 3 expansion with two additional berths. Vessel turnaround times expected to improve 20%.", source: "TradeWinds", relevance: 4, implication: "Reduced port congestion benefits VOFF operational efficiency and scheduling" },
  { section: "MARKET WATCH — GUYANA", sectionIcon: "🇬🇾", date: "Apr 10, 2026", title: "Bourbon Offshore Wins Guyana CTV Extension", summary: "Bourbon Offshore secured a 12-month CTV contract extension with ExxonMobil for Liza Unity FPSO operations. Two vessels confirmed.", source: "Rigzone", relevance: 4, implication: "Competitor intel — Bourbon holds strong ExxonMobil position. Target alternative FPSOs for entry." },
  { section: "MARKET WATCH — WEST AFRICA", sectionIcon: "🇦🇴", date: "Apr 13, 2026", title: "Azule Energy Increases Block 15 Production Targets", summary: "Azule Energy announced revised 2027 production targets for Angola Block 15, including two new satellite well tiebacks requiring increased marine logistics.", source: "Africa Oil & Gas Report", relevance: 5, implication: "Increased CTV demand in Angola — engage ALS for Azule tender intelligence" },
  { section: "MARKET WATCH — WEST AFRICA", sectionIcon: "🇳🇬", date: "Apr 11, 2026", title: "NIMASA Updates Cabotage Waiver Process", summary: "NIMASA has streamlined the foreign-flag vessel cabotage waiver application process, reducing processing times from 90 to 45 days.", source: "NIMASA Gazette", relevance: 3, implication: "Improved market access timeline for VOFF in Nigerian waters" },
  { section: "REGULATORY & COMPLIANCE", sectionIcon: "⚖️", date: "Apr 14, 2026", title: "IMCA Safety Flash 04/26 — Gangway Failure During Transfer", summary: "IMCA published Safety Flash 04/26 detailing a gangway structural failure during crew transfer on a North Sea CTV. Root cause: inadequate inspection regime.", source: "IMCA", relevance: 5, implication: "Issue VOFF Safety Bulletin immediately — review all vessel gangway inspection logs" },
  { section: "REGULATORY & COMPLIANCE", sectionIcon: "⚖️", date: "Apr 12, 2026", title: "IMO MEPC 84 Adopts Mid-Term GHG Measures", summary: "IMO Marine Environment Protection Committee adopted mid-term measures including fuel standard and economic mechanism for greenhouse gas reduction.", source: "IMO Press", relevance: 3, implication: "Monitor methanol/dual-fuel requirements timeline — align with VOFF-CC chemical carrier concept" },
  { section: "TECHNOLOGY & VESSELS", sectionIcon: "🚢", date: "Apr 13, 2026", title: "Strategic Marine Delivers 12th AIRCAT 35 Unit", summary: "Strategic Marine's Vung Tau yard completed delivery of the 12th AIRCAT 35 SES vessel, continuing the high-speed CTV production line.", source: "Strategic Marine", relevance: 4, implication: "Production pipeline remains active — VOFF newbuild slots available for 2027 delivery" },
  { section: "COMPETITOR WATCH", sectionIcon: "👁️", date: "Apr 14, 2026", title: "Tidewater Reports Q1 2026 Earnings — Marine Division Up 14%", summary: "Tidewater's marine transportation segment reported 14% revenue growth driven by West Africa and Guyana operations. Fleet utilisation at 87%.", source: "Tidewater Investor Relations", relevance: 3, implication: "Confirms market strength in VOFF target regions — rates holding firm" },
];

const SECTIONS = [...new Set(STORIES.map(s => s.section))];

/* ── Sources ── */
const SOURCES = [
  { name: "Offshore Energy Today", category: "Market", frequency: "Weekly", status: "Active", lastChecked: "2026-04-14" },
  { name: "TradeWinds", category: "Market", frequency: "Weekly", status: "Active", lastChecked: "2026-04-14" },
  { name: "Rigzone", category: "Market", frequency: "Weekly", status: "Active", lastChecked: "2026-04-13" },
  { name: "IMCA Safety Flashes", category: "Regulatory", frequency: "On publish", status: "Active", lastChecked: "2026-04-14" },
  { name: "MAIB Investigation Reports", category: "Regulatory", frequency: "Monthly", status: "Active", lastChecked: "2026-04-01" },
  { name: "RMI/IRI Notices", category: "Regulatory", frequency: "Monthly", status: "Active", lastChecked: "2026-04-01" },
  { name: "IMO Circulars (MSC/MEPC)", category: "Regulatory", frequency: "Monthly", status: "Active", lastChecked: "2026-04-01" },
  { name: "ANPG (Angola) News", category: "Market", frequency: "Weekly", status: "Active", lastChecked: "2026-04-12" },
  { name: "ExxonMobil Press Releases", category: "Commercial", frequency: "Weekly", status: "Active", lastChecked: "2026-04-14" },
  { name: "Azule Energy News", category: "Commercial", frequency: "Weekly", status: "Active", lastChecked: "2026-04-13" },
];

const IntelligenceDigest = () => {
  const [tab, setTab] = useState("digest");
  const [loading, setLoading] = useState(false);
  const [contentGenerated, setContentGenerated] = useState(false);

  const handleGenerate = () => {
    setLoading(true);
    toast.info("Scanning intelligence sources...");
    setTimeout(() => { setLoading(false); toast.success("Digest updated with latest intelligence"); }, 3000);
  };

  const handleGenerateContent = () => {
    setContentGenerated(true);
    toast.success("Content generated — 2 variations ready");
  };

  return (
    <IntranetLayout>
      {/* Header */}
      <div className="bg-[#00283C] rounded-xl p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <h1 className="text-white text-2xl font-bold tracking-tight font-display">VOFF INTELLIGENCE</h1>
            <p className="text-white/50 text-sm font-body mt-1 italic">Weekly Briefing | Market Intelligence | Content Engine</p>
          </div>
          <div className="flex items-center gap-3 text-xs text-white/40 font-body">
            <span>Issue No. Q2 2026 — Week 16</span>
            <span>|</span>
            <span>Next digest: Friday</span>
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="font-body mb-6">
          <TabsTrigger value="digest"><Newspaper className="w-4 h-4 mr-1.5" /> Weekly Digest</TabsTrigger>
          <TabsTrigger value="archive"><Archive className="w-4 h-4 mr-1.5" /> Archive</TabsTrigger>
          <TabsTrigger value="content"><PenTool className="w-4 h-4 mr-1.5" /> Content Engine</TabsTrigger>
          <TabsTrigger value="sources"><Settings className="w-4 h-4 mr-1.5" /> Sources</TabsTrigger>
        </TabsList>

        {/* Weekly Digest */}
        <TabsContent value="digest">
          <div className="flex items-center justify-between mb-6">
            <div className="border-t-2 border-b-2 border-[#288CC8] py-2 px-4">
              <p className="text-xs text-muted-foreground font-body">VOFF INTELLIGENCE DIGEST</p>
              <p className="text-xs text-muted-foreground font-body">Week of April 14, 2026 | Prepared by VOFF Navigator AI</p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={handleGenerate} className="font-body text-xs">
                <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
              </Button>
              <Button size="sm" variant="outline" onClick={() => toast.success("Digest emailed to leadership")} className="font-body text-xs">
                <Mail className="w-3.5 h-3.5 mr-1" /> Email Digest
              </Button>
              <Button size="sm" variant="outline" onClick={() => toast.success("PDF exported")} className="font-body text-xs">
                <Download className="w-3.5 h-3.5 mr-1" /> Export PDF
              </Button>
            </div>
          </div>

          {SECTIONS.map(section => (
            <div key={section} className="mb-8">
              <h2 className="text-sm font-bold uppercase tracking-wider text-[#288CC8] font-display mb-4 flex items-center gap-2">
                <span>{STORIES.find(s => s.section === section)?.sectionIcon}</span> {section}
              </h2>
              <div className="space-y-4">
                {STORIES.filter(s => s.section === section).map((story, i) => (
                  <div key={i} className="bg-card border border-border rounded-xl p-5 hover:border-[#288CC8]/30 transition-colors">
                    <div className="flex items-start justify-between mb-2">
                      <span className="text-[10px] text-muted-foreground font-body">{story.date}</span>
                      <div className="flex items-center gap-0.5">
                        {Array.from({ length: 5 }, (_, si) => (
                          <Star key={si} className={`w-3 h-3 ${si < story.relevance ? "text-amber-400 fill-amber-400" : "text-muted-foreground/20"}`} />
                        ))}
                      </div>
                    </div>
                    <h3 className="text-sm font-bold text-foreground font-body mb-2">{story.title}</h3>
                    <p className="text-xs text-foreground/70 font-body mb-3">{story.summary}</p>
                    <div className="flex items-center gap-2 text-[10px] text-muted-foreground font-body mb-2">
                      <Globe className="w-3 h-3" /> Source: {story.source}
                    </div>
                    <div className="bg-[#288CC8]/5 border border-[#288CC8]/20 rounded-lg p-3 mt-2">
                      <p className="text-[10px] uppercase tracking-wider text-[#288CC8] font-bold font-body mb-1">VOFF Relevance: {"★".repeat(story.relevance)}{"☆".repeat(5 - story.relevance)}</p>
                      <p className="text-xs text-foreground font-body italic">"{story.implication}"</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </TabsContent>

        {/* Archive */}
        <TabsContent value="archive">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { issue: "Q2 2026 — Week 15", date: "Apr 7, 2026", headlines: ["ExxonMobil Yellowtail FPSO ramp-up", "IMCA Alert M-221 issued", "Tidewater fleet expansion"] },
              { issue: "Q2 2026 — Week 14", date: "Mar 31, 2026", headlines: ["Azule Energy Q1 review", "ABS Class rule update", "Bourbon Angola contract"] },
              { issue: "Q1 2026 — Week 13", date: "Mar 24, 2026", headlines: ["IMO MEPC 83 outcomes", "Strategic Marine delivery update", "GSBI expansion plan"] },
            ].map((d, i) => (
              <div key={i} className="bg-card border border-border rounded-xl p-5">
                <p className="text-xs text-[#288CC8] font-bold font-body mb-1">{d.issue}</p>
                <p className="text-[10px] text-muted-foreground font-body mb-3">{d.date}</p>
                <ul className="space-y-1 mb-3">
                  {d.headlines.map((h, hi) => <li key={hi} className="text-xs text-foreground font-body">• {h}</li>)}
                </ul>
                <Button onClick={() => toast.success("Action recorded: Read")} size="sm" variant="outline" className="w-full font-body text-xs">Read</Button>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Content Engine */}
        <TabsContent value="content">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            <div className="lg:col-span-2 space-y-4">
              <h3 className="text-sm font-bold text-foreground font-body">Milestone Input</h3>
              <Select><SelectTrigger className="font-body text-sm"><SelectValue placeholder="Milestone Type" /></SelectTrigger>
                <SelectContent>{["Contract Award", "Vessel Delivery", "Partnership / MOU Signed", "Certification Achieved", "Team / Hiring Announcement", "Industry Event", "Company Milestone", "Technical Innovation", "Award / Recognition"].map(t => <SelectItem key={t} value={t.toLowerCase().replace(/ /g, "_")}>{t}</SelectItem>)}</SelectContent></Select>
              <Textarea placeholder="What happened? e.g., Signed MOU with Kongsberg Maritime..." className="font-body text-sm min-h-[80px]" />
              <Input type="date" className="font-body text-sm" />
              <Input placeholder="Key parties involved" className="font-body text-sm" />
              <Select><SelectTrigger className="font-body text-sm"><SelectValue placeholder="Author Voice" /></SelectTrigger>
                <SelectContent><SelectItem value="jerome">Jerome Arnold — CEO</SelectItem><SelectItem value="kyle">Kyle Pemberton — COO</SelectItem><SelectItem value="company">Velocity Offshore (company)</SelectItem></SelectContent></Select>
              <Select><SelectTrigger className="font-body text-sm"><SelectValue placeholder="Platform" /></SelectTrigger>
                <SelectContent><SelectItem value="linkedin_post">LinkedIn Post</SelectItem><SelectItem value="linkedin_article">LinkedIn Article</SelectItem><SelectItem value="twitter">Twitter/X</SelectItem><SelectItem value="press">Press Release</SelectItem></SelectContent></Select>
              <Button onClick={handleGenerateContent} className="w-full bg-[#288CC8] hover:bg-[#288CC8]/80 font-body">Generate Content</Button>
            </div>
            <div className="lg:col-span-3 space-y-4">
              {contentGenerated ? (
                <>
                  {["Bold", "Measured"].map(variant => (
                    <div key={variant} className="bg-card border border-border rounded-xl p-5">
                      <div className="flex items-center justify-between mb-3">
                        <Badge variant="outline" className="text-[10px] font-bold">{variant.toUpperCase()} VARIATION</Badge>
                        <div className="flex gap-1">
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => toast.success("Copied to clipboard")}><Copy className="w-3.5 h-3.5" /></Button>
                        </div>
                      </div>
                      <div className="bg-muted/30 rounded-lg p-4 border border-border">
                        <p className="text-sm font-body text-foreground whitespace-pre-line">
                          {variant === "Bold"
                            ? "🚀 Another milestone in our journey to redefine offshore crew transfer.\n\nVelocity Offshore has signed a Memorandum of Understanding with Kongsberg Maritime to co-develop next-generation vessel monitoring systems for our AIRCAT 35 SES fleet.\n\nThis isn't just a technology partnership — it's a commitment to the safest, most efficient crew transfer operations in the industry.\n\nWhen you combine Kongsberg's world-leading maritime technology with VOFF's operational expertise, the result is a step-change in what clients can expect from their CTV provider.\n\n#OffshoreEnergy #MaritimeIndustry #CrewTransfer #SurfaceEffectShip #Innovation"
                            : "Velocity Offshore Holdings is pleased to announce a Memorandum of Understanding with Kongsberg Maritime for the integration of advanced vessel monitoring and DP systems across our AIRCAT 35 SES fleet.\n\nThis partnership brings together Kongsberg's industry-leading K-Pos DP2 and K-Chief engine monitoring platforms with our high-speed SES operations, enhancing safety and operational transparency for our clients.\n\nWe look forward to delivering enhanced capability across our Guyana and West Africa operations.\n\n#OffshoreEnergy #MaritimeIndustry #CrewTransfer"
                          }
                        </p>
                      </div>
                      <p className="text-[10px] text-muted-foreground font-body mt-2">Character count: {variant === "Bold" ? "587" : "498"} / 3,000</p>
                    </div>
                  ))}
                </>
              ) : (
                <div className="flex items-center justify-center h-64 bg-muted/20 rounded-xl border border-dashed border-border">
                  <p className="text-sm text-muted-foreground font-body">Enter milestone details and click Generate</p>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Sources */}
        <TabsContent value="sources">
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm font-body">
                <thead>
                  <tr className="bg-muted/50 text-muted-foreground text-[11px] uppercase tracking-wider">
                    <th className="text-left px-4 py-3">Source</th>
                    <th className="text-left px-4 py-3">Category</th>
                    <th className="text-left px-4 py-3">Frequency</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-left px-4 py-3">Last Checked</th>
                  </tr>
                </thead>
                <tbody>
                  {SOURCES.map((s, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="px-4 py-3 font-medium text-foreground">{s.name}</td>
                      <td className="px-4 py-3 text-muted-foreground">{s.category}</td>
                      <td className="px-4 py-3 text-muted-foreground">{s.frequency}</td>
                      <td className="px-4 py-3"><Badge variant="outline" className="text-[10px] bg-emerald-500/15 text-emerald-400 border-emerald-500/30">🟢 Active</Badge></td>
                      <td className="px-4 py-3 text-muted-foreground text-xs">{s.lastChecked}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </IntranetLayout>
  );
};

export default IntelligenceDigest;
